import React, { memo } from 'react';
import { motion } from 'framer-motion';
import ChessPiece from './ChessPiece';
import { AlertTriangle } from 'lucide-react';

const Square = memo(({ 
  square, 
  piece, 
  isLight, 
  isSelected, 
  isLegalMove, 
  isLastMove,
  isSuggested,
  isInCheck,
  isInDanger,
  dangerSeverity,
  showCoordinates,
  file,
  rank,
  theme,
  onClick
}) => {
  const getSquareColor = () => {
    if (theme === 'cosmic') {
      return isLight 
        ? 'bg-gradient-to-br from-indigo-900/80 to-purple-900/80' 
        : 'bg-gradient-to-br from-slate-900 to-indigo-950';
    }
    // Classic wooden theme
    return isLight 
      ? 'bg-amber-200' 
      : 'bg-amber-700';
  };

  const getHighlightStyle = () => {
    // Check indicator takes priority - king in danger!
    if (isInCheck) {
      return 'ring-4 ring-inset ring-red-500 bg-red-500/40 animate-pulse';
    }
    if (isSelected) {
      return 'ring-4 ring-inset ring-cyan-400/70';
    }
    if (isLastMove) {
      return theme === 'cosmic' 
        ? 'bg-yellow-500/30' 
        : 'bg-yellow-400/40';
    }
    if (isSuggested) {
      return 'bg-cyan-500/40';
    }
    return '';
  };

  // Get danger highlight style based on severity
  const getDangerStyle = () => {
    if (!isInDanger) return '';
    
    switch (dangerSeverity) {
      case 'high': // Undefended piece
        return 'ring-3 ring-inset ring-red-500/80';
      case 'medium': // Would lose material in exchange
        return 'ring-2 ring-inset ring-orange-500/70';
      case 'low': // Attacked but defended
        return 'ring-2 ring-inset ring-yellow-500/50';
      default:
        return '';
    }
  };

  return (
    <motion.div
      className={`
        relative aspect-square flex items-center justify-center
        ${getSquareColor()}
        ${getHighlightStyle()}
        ${getDangerStyle()}
        ${isSelected ? 'z-10' : ''}
        transition-colors duration-150
      `}
      onClick={onClick}
      whileHover={{ brightness: 1.1 }}
      data-testid={`square-${square}`}
      data-square={square}
    >
      {/* Check warning overlay */}
      {isInCheck && (
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="absolute inset-0 bg-gradient-radial from-red-500/60 via-red-500/30 to-transparent"
        />
      )}
      
      {/* Danger highlight overlay */}
      {isInDanger && !isInCheck && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`
            absolute inset-0
            ${dangerSeverity === 'high' 
              ? 'bg-gradient-radial from-red-500/40 via-red-500/20 to-transparent' 
              : dangerSeverity === 'medium'
                ? 'bg-gradient-radial from-orange-500/30 via-orange-500/15 to-transparent'
                : 'bg-gradient-radial from-yellow-500/25 via-yellow-500/10 to-transparent'
            }
          `}
        />
      )}
      
      {/* Danger indicator icon for high severity */}
      {isInDanger && dangerSeverity === 'high' && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 500, damping: 25 }}
          className="absolute top-0.5 right-0.5 z-30"
        >
          <div className="bg-red-500 rounded-full p-0.5 shadow-lg animate-pulse">
            <AlertTriangle className="w-2.5 h-2.5 text-white" />
          </div>
        </motion.div>
      )}
      
      {/* Warning dot for medium severity */}
      {isInDanger && dangerSeverity === 'medium' && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute top-0.5 right-0.5 z-30"
        >
          <div className="w-2 h-2 bg-orange-500 rounded-full shadow-md animate-pulse" />
        </motion.div>
      )}
      
      {/* Legal move indicator */}
      {isLegalMove && !piece && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className={`
            w-[30%] h-[30%] rounded-full
            ${theme === 'cosmic' 
              ? 'bg-cyan-400/60 shadow-[0_0_10px_rgba(0,255,255,0.5)]' 
              : 'bg-green-500/50'
            }
          `}
        />
      )}
      
      {/* Legal move indicator for capture */}
      {isLegalMove && piece && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className={`
            absolute inset-0 rounded-full m-1
            ${theme === 'cosmic'
              ? 'ring-4 ring-red-500/60 shadow-[0_0_15px_rgba(255,0,0,0.3)]'
              : 'ring-4 ring-red-500/50'
            }
          `}
        />
      )}
      
      {/* Suggestion arrow target */}
      {isSuggested && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-cyan-400/30 animate-pulse"
        />
      )}
      
      {/* Chess piece */}
      {piece && (
        <ChessPiece 
          piece={piece} 
          isSelected={isSelected}
        />
      )}
      
      {/* Coordinates */}
      {showCoordinates && (
        <>
          {file === 0 && (
            <span className={`
              absolute top-0.5 left-1 text-[10px] font-bold
              ${theme === 'cosmic' 
                ? (isLight ? 'text-indigo-300' : 'text-purple-400')
                : (isLight ? 'text-amber-800' : 'text-amber-100')
              }
            `}>
              {rank}
            </span>
          )}
          {rank === 1 && (
            <span className={`
              absolute bottom-0.5 right-1 text-[10px] font-bold
              ${theme === 'cosmic'
                ? (isLight ? 'text-indigo-300' : 'text-purple-400')
                : (isLight ? 'text-amber-800' : 'text-amber-100')
              }
            `}>
              {String.fromCharCode(97 + file)}
            </span>
          )}
        </>
      )}
    </motion.div>
  );
});

Square.displayName = 'Square';

export default Square;
